import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import PeopleIcon from "@material-ui/icons/People";
import TodayIcon from "@material-ui/icons/Today";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";
import ListAltIcon from "@material-ui/icons/ListAlt";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { companyTz, formatDateTime, publicFileUrl } from "./helpers";
import { tNegocio, useMobile } from "./shared";
import { useNegocioResource } from "./Lists";

const t = (key) => tNegocio(`pages.dashboard.codeLog.${key}`);
const REFRESH_MS = 60000;

const OK_COLOR = "#16a34a";
const FAIL_COLOR = "#dc2626";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    "@keyframes codeLogRise": {
      from: { opacity: 0, transform: "translateY(10px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes codeLogPulse": {
      "0%": { boxShadow: `0 0 0 0 ${alpha(OK_COLOR, 0.45)}` },
      "70%": { boxShadow: `0 0 0 8px ${alpha(OK_COLOR, 0)}` },
      "100%": { boxShadow: `0 0 0 0 ${alpha(OK_COLOR, 0)}` },
    },
    "@keyframes codeLogSpin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    root: { marginBottom: theme.spacing(3) },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      flexWrap: "wrap",
      margin: theme.spacing(1, 0, 1.5),
    },
    title: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    live: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: OK_COLOR,
      animation: "$codeLogPulse 2s infinite",
    },
    headerActions: { display: "flex", alignItems: "center", gap: 4 },
    spinning: { animation: "$codeLogSpin 0.8s linear infinite" },
    kpis: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 8,
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 },
    },
    kpi: {
      position: "relative",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 14px",
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      animation: "$codeLogRise 0.45s ease both",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[3] },
    },
    kpiIcon: {
      display: "grid",
      placeItems: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      flexShrink: 0,
      color: theme.palette.primary.main,
      background: alpha(theme.palette.primary.main, dark ? 0.2 : 0.1),
    },
    kpiLabel: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.2 },
    kpiValue: { fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: theme.palette.text.primary, fontVariantNumeric: "tabular-nums" },
    kpiHint: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 },
    ring: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" },
    charts: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.6fr) minmax(260px, 1fr)",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", gap: theme.spacing(1.5) },
    },
    chartsThree: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", gap: theme.spacing(1.5) },
    },
    panel: {
      padding: theme.spacing(2),
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      minWidth: 0,
      animation: "$codeLogRise 0.5s ease both",
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1.5) },
    },
    panelTitle: { fontSize: 14, fontWeight: 700, marginBottom: 2, color: theme.palette.text.primary },
    panelSub: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: theme.spacing(1.5) },
    donutWrap: { position: "relative", width: "100%", height: 180 },
    donutCenter: {
      position: "absolute",
      inset: 0,
      display: "grid",
      placeItems: "center",
      pointerEvents: "none",
      textAlign: "center",
    },
    donutValue: { fontSize: 26, fontWeight: 800, color: theme.palette.text.primary },
    donutLabel: { fontSize: 11, color: theme.palette.text.secondary },
    legend: { display: "flex", justifyContent: "center", gap: 16, fontSize: 12, marginTop: 4, color: theme.palette.text.secondary },
    dot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 6, verticalAlign: "middle" },
    reasons: { marginTop: theme.spacing(1.5), display: "grid", gap: 6 },
    reason: { display: "flex", justifyContent: "space-between", fontSize: 12, color: theme.palette.text.secondary, gap: 8 },
    barRow: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 4, marginBottom: 10 },
    barLabel: { fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: theme.palette.text.primary },
    barValue: { fontSize: 12, color: theme.palette.text.secondary, fontVariantNumeric: "tabular-nums" },
    barTrack: {
      gridColumn: "1 / -1",
      height: 8,
      borderRadius: 99,
      overflow: "hidden",
      background: alpha(theme.palette.text.primary, dark ? 0.12 : 0.06),
      display: "flex",
    },
    barFill: { height: "100%", transition: "width 0.9s cubic-bezier(.2,.8,.2,1)" },
    empty: { fontSize: 13, color: theme.palette.text.secondary, padding: theme.spacing(2, 0), textAlign: "center" },
    tableWrap: {
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      overflowX: "auto",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 880 },
    th: {
      textAlign: "left",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: "10px 12px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
    },
    tr: {
      animation: "$codeLogRise 0.4s ease both",
      transition: "background 0.15s ease",
      "&:hover": { background: alpha(theme.palette.primary.main, dark ? 0.12 : 0.04) },
      "& td": { padding: "10px 12px", borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: "middle" },
      "&:last-child td": { borderBottom: "none" },
    },
    person: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, cursor: "pointer" },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      flexShrink: 0,
      display: "grid",
      placeItems: "center",
      fontSize: 12,
      fontWeight: 800,
      color: "#fff",
    },
    strong: { fontWeight: 600, color: theme.palette.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 },
    muted: { fontSize: 12, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    product: { display: "flex", alignItems: "center", gap: 8 },
    productImg: { width: 26, height: 26, borderRadius: 6, objectFit: "cover", flexShrink: 0, background: theme.palette.action.hover },
    mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: theme.palette.text.primary, whiteSpace: "nowrap" },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 9px",
      borderRadius: 99,
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
    chipOk: { color: OK_COLOR, background: alpha(OK_COLOR, dark ? 0.2 : 0.1) },
    chipFail: { color: FAIL_COLOR, background: alpha(FAIL_COLOR, dark ? 0.22 : 0.1) },
    channel: {
      display: "inline-block",
      marginTop: 3,
      padding: "1px 7px",
      borderRadius: 6,
      fontSize: 11,
      color: theme.palette.text.secondary,
      background: theme.palette.action.hover,
    },
    flag: { fontSize: 16, marginRight: 6, verticalAlign: "middle" },
    cards: { display: "grid", gap: 8 },
    card: {
      padding: theme.spacing(1.5),
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      animation: "$codeLogRise 0.4s ease both",
      display: "grid",
      gap: 6,
    },
    cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
    cardLine: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, color: theme.palette.text.secondary },
    filters: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginBottom: theme.spacing(1.5), "& > *": { minWidth: 160 } },
    dialogTitle: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  };
});

/* ---------- helpers ---------- */

const AVATAR_COLORS = ["#6366f1", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899", "#8b5cf6", "#ef4444", "#22c55e"];
const avatarColor = (seed) => AVATAR_COLORS[Math.abs(String(seed || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];
const initials = (name) => String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const flagOf = (cc) => (/^[A-Z]{2}$/.test(cc || "") ? String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0))) : "🌐");
const countryName = (cc) => {
  if (!cc) return "";
  try {
    return new Intl.DisplayNames([(i18n.language || "es").slice(0, 2)], { type: "region" }).of(cc) || cc;
  } catch {
    return cc;
  }
};
const locationText = (row) => {
  const place = [row.city, row.region].filter(Boolean).join(", ");
  const country = countryName(row.country);
  return [place, country].filter(Boolean).join(" · ");
};

const relative = (value) => {
  if (!value) return "";
  return moment(value).locale((i18n.language || "es").slice(0, 2)).fromNow();
};

const channelLabel = (channel) => t(`channel_${channel || "operator"}`);

/** Cuenta desde 0 hasta el valor con easing; se reinicia cuando cambia. */
const useCountUp = (value, duration = 900) => {
  const [shown, setShown] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const target = Number(value) || 0;
    const start = performance.now();
    const origin = from.current;
    let frame;
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(origin + (target - origin) * eased);
      if (p < 1) frame = requestAnimationFrame(step);
      else from.current = target;
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);
  return shown;
};

/* ---------- piezas ---------- */

function Kpi({ icon: Icon, label, value, format = (n) => Math.round(n).toLocaleString(), hint, delay = 0, children }) {
  const classes = useStyles();
  const animated = useCountUp(value);
  return (
    <div className={classes.kpi} style={{ animationDelay: `${delay}ms` }}>
      <span className={classes.kpiIcon}><Icon fontSize="small" /></span>
      <div style={{ minWidth: 0 }}>
        <div className={classes.kpiLabel}>{label}</div>
        <div className={classes.kpiValue}>{format(animated)}</div>
        {hint ? <div className={classes.kpiHint}>{hint}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Ring({ value, size = 44 }) {
  const theme = useTheme();
  const animated = useCountUp(value * 100, 1100);
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={theme.palette.divider} strokeWidth="5" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={OK_COLOR}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - (c * animated) / 100}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function Bars({ rows, labelOf, max }) {
  const classes = useStyles();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  if (!rows.length) return <div className={classes.empty}>{t("noData")}</div>;
  return rows.map((row, index) => {
    const okPct = mounted ? (row.ok / max) * 100 : 0;
    const failPct = mounted ? ((row.total - row.ok) / max) * 100 : 0;
    return (
      <div key={index} className={classes.barRow}>
        <div className={classes.barLabel} title={labelOf(row)}>{labelOf(row)}</div>
        <div className={classes.barValue}>{row.total} · {Math.round((row.ok / (row.total || 1)) * 100)}%</div>
        <div className={classes.barTrack}>
          <div className={classes.barFill} style={{ width: `${okPct}%`, background: OK_COLOR, transitionDelay: `${index * 60}ms` }} />
          <div className={classes.barFill} style={{ width: `${failPct}%`, background: alpha(FAIL_COLOR, 0.75), transitionDelay: `${index * 60}ms` }} />
        </div>
      </div>
    );
  });
}

function ResultChip({ row }) {
  const classes = useStyles();
  const chip = (
    <span className={`${classes.chip} ${row.ok ? classes.chipOk : classes.chipFail}`}>
      {row.ok ? <CheckCircleIcon style={{ fontSize: 14 }} /> : <CancelIcon style={{ fontSize: 14 }} />}
      {row.ok ? t("found") : t("notFound")}
    </span>
  );
  return row.reason ? <Tooltip title={row.reason} arrow>{chip}</Tooltip> : chip;
}

function Person({ row, onOpen }) {
  const classes = useStyles();
  const name = row.contact?.name || t("unknownCustomer");
  return (
    <div className={classes.person} onClick={() => row.contact && onOpen(row.contact.id)} role={row.contact ? "button" : undefined}>
      <span className={classes.avatar} style={{ background: avatarColor(row.contact?.id || name) }}>{initials(name)}</span>
      <div style={{ minWidth: 0 }}>
        <div className={classes.strong} title={name}>{name}</div>
        <div className={classes.muted}>{row.contact?.number || "—"}</div>
      </div>
    </div>
  );
}

function Location({ row }) {
  const classes = useStyles();
  if (!row.ipAddress && !row.country) {
    return <span className={classes.muted}>{row.channel === "chat" ? t("viaChat") : "—"}</span>;
  }
  return (
    <div>
      <div className={classes.mono}>{row.ipAddress || "—"}</div>
      {row.country ? (
        <div className={classes.muted}>
          <span className={classes.flag}>{flagOf(row.country)}</span>
          {locationText(row)}
        </div>
      ) : null}
    </div>
  );
}

function LookupRows({ rows, onOpenContact }) {
  const classes = useStyles();
  const isMobile = useMobile();
  if (!rows.length) return <div className={classes.empty}>{t("empty")}</div>;
  if (isMobile) {
    return (
      <div className={classes.cards}>
        {rows.map((row, index) => (
          <div key={row.id} className={classes.card} style={{ animationDelay: `${index * 40}ms` }}>
            <div className={classes.cardTop}>
              <Person row={row} onOpen={onOpenContact} />
              <ResultChip row={row} />
            </div>
            <div className={classes.cardLine}><span>{row.product?.name || "—"}</span><span>{formatDateTime(row.createdAt)}</span></div>
            <div className={classes.cardLine}><span className={classes.mono}>{row.accountEmail}</span><span>{channelLabel(row.channel)}</span></div>
            <Location row={row} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>{t("colCustomer")}</th>
            <th className={classes.th}>{t("colProduct")}</th>
            <th className={classes.th}>{t("colEmail")}</th>
            <th className={classes.th}>{t("colResult")}</th>
            <th className={classes.th}>{t("colDate")}</th>
            <th className={classes.th}>{t("colIp")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id} className={classes.tr} style={{ animationDelay: `${index * 35}ms` }}>
              <td><Person row={row} onOpen={onOpenContact} /></td>
              <td>
                <div className={classes.product}>
                  {row.product?.imageUrl ? <img className={classes.productImg} src={publicFileUrl(row.product.imageUrl)} alt="" loading="lazy" /> : null}
                  <span className={classes.strong} title={row.product?.name}>{row.product?.name || "—"}</span>
                </div>
              </td>
              <td><span className={classes.mono}>{row.accountEmail}</span></td>
              <td><ResultChip row={row} /></td>
              <td>
                <Tooltip title={relative(row.createdAt)} arrow>
                  <div className={classes.muted} style={{ color: "inherit" }}>{formatDateTime(row.createdAt)}</div>
                </Tooltip>
                <span className={classes.channel}>{channelLabel(row.channel)}</span>
              </td>
              <td><Location row={row} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- registro completo ---------- */

function FullLogDialog({ open, onClose, onOpenContact }) {
  const classes = useStyles();
  const isMobile = useMobile();
  const [filters, setFilters] = useState({ search: "", ok: "", channel: "" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ records: [], count: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setFilters((f) => (f.search === search ? f : { ...f, search })), 350);
    return () => clearTimeout(id);
  }, [search]);
  useEffect(() => setPage(0), [filters, pageSize]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    api
      .get("/negocio/codes/log", { params: { ...filters, page: page + 1, pageSize } })
      .then(({ data: result }) => { if (active) setData(result); })
      .catch((error) => { if (active) toastError(error); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, filters, page, pageSize]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <span className={classes.panelTitle} style={{ fontSize: 18 }}>{t("fullTitle")}</span>
        <IconButton size="small" onClick={onClose} aria-label={t("close")}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.filters}>
          <TextField size="small" variant="outlined" label={t("searchLabel")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: "2 1 220px" }} />
          <TextField select size="small" variant="outlined" label={t("colResult")} value={filters.ok} onChange={(e) => setFilters((f) => ({ ...f, ok: e.target.value }))}>
            <MenuItem value="">{t("all")}</MenuItem>
            <MenuItem value="1">{t("found")}</MenuItem>
            <MenuItem value="0">{t("notFound")}</MenuItem>
          </TextField>
          <TextField select size="small" variant="outlined" label={t("colChannel")} value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}>
            <MenuItem value="">{t("all")}</MenuItem>
            {["landing", "store", "chat", "panel"].map((c) => <MenuItem key={c} value={c}>{channelLabel(c)}</MenuItem>)}
          </TextField>
        </div>
        <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s ease" }}>
          <LookupRows key={`${page}-${JSON.stringify(filters)}`} rows={data.records || []} onOpenContact={(id) => { onClose(); onOpenContact(id); }} />
        </div>
        <TablePagination
          component="div"
          count={data.count || 0}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t("perPage")}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ---------- sección ---------- */

export default function CodeLookupLog() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);
  const { value: stats, loading, reload } = useNegocioResource("codeLookupStats", async () => {
    const { data } = await api.get("/negocio/codes/log/stats");
    return data;
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") reload?.({ soft: true });
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  const refresh = async () => {
    setRefreshing(true);
    try { await reload?.({ soft: true }); } finally { setRefreshing(false); }
  };

  const openContact = (contactId) => history.push(`/negocio/clientes?contactId=${contactId}`);
  const tz = companyTz();
  const daily = useMemo(
    () => (stats?.daily || []).map((d) => ({ ...d, label: moment.tz(d.date, tz).format(isMobile ? "D/M" : "ddd D") })),
    [stats, tz, isMobile]
  );
  const hours = stats?.hours || [];
  const donut = [
    { name: t("found"), value: stats?.ok || 0, color: OK_COLOR },
    { name: t("notFound"), value: stats?.fail || 0, color: FAIL_COLOR },
  ];
  const maxProduct = Math.max(1, ...(stats?.byProduct || []).map((r) => r.total));
  const maxCountry = Math.max(1, ...(stats?.byCountry || []).map((r) => r.total));
  const maxChannel = Math.max(1, ...(stats?.byChannel || []).map((r) => r.total));
  const axis = { stroke: theme.palette.text.secondary, tick: { fontSize: isMobile ? 10 : 11 } };
  const tooltipStyle = { contentStyle: { borderRadius: 10, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper, fontSize: 12 } };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.title}><span className={classes.live} />{t("title")}</div>
        <div className={classes.headerActions}>
          <Tooltip title={t("refresh")} arrow>
            <IconButton size="small" onClick={refresh} aria-label={t("refresh")}>
              <RefreshIcon fontSize="small" className={refreshing ? classes.spinning : undefined} />
            </IconButton>
          </Tooltip>
          <Button size="small" color="primary" variant="outlined" startIcon={<ListAltIcon />} onClick={() => setOpen(true)}>
            {t("viewAll")}
          </Button>
        </div>
      </div>

      <div className={classes.kpis}>
        <Kpi icon={VpnKeyIcon} label={t("kpiTotal").replace("{days}", stats?.days || 30)} value={stats?.total} delay={0} />
        <Kpi
          icon={CheckCircleIcon}
          label={t("kpiRate")}
          value={(stats?.successRate || 0) * 100}
          format={(n) => `${Math.round(n)}%`}
          hint={`${stats?.ok || 0} ${t("found").toLowerCase()}`}
          delay={70}
        >
          {!isMobile ? <span className={classes.ring}><Ring value={stats?.successRate || 0} /></span> : null}
        </Kpi>
        <Kpi icon={PeopleIcon} label={t("kpiCustomers")} value={stats?.uniqueCustomers} delay={140} />
        <Kpi
          icon={TodayIcon}
          label={t("kpiToday")}
          value={stats?.today}
          hint={stats?.lastOkAt ? `${t("lastOk")} ${relative(stats.lastOkAt)}` : null}
          delay={210}
        />
      </div>

      <div className={classes.charts}>
        <div className={classes.panel}>
          <div className={classes.panelTitle}>{t("dailyTitle")}</div>
          <div className={classes.panelSub}>{t("dailySub")}</div>
          <div style={{ width: "100%", height: isMobile ? 200 : 230 }}>
            <ResponsiveContainer>
              <BarChart data={daily} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="label" {...axis} interval={isMobile ? 1 : 0} />
                <YAxis allowDecimals={false} {...axis} />
                <ChartTooltip {...tooltipStyle} cursor={{ fill: alpha(theme.palette.primary.main, 0.06) }} />
                <Bar dataKey="ok" name={t("found")} stackId="a" fill={OK_COLOR} animationDuration={900} />
                <Bar dataKey="fail" name={t("notFound")} stackId="a" fill={FAIL_COLOR} radius={[5, 5, 0, 0]} animationDuration={900} animationBegin={200} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={classes.panel} style={{ animationDelay: "80ms" }}>
          <div className={classes.panelTitle}>{t("donutTitle")}</div>
          <div className={classes.panelSub}>{t("kpiTotal").replace("{days}", stats?.days || 30)}</div>
          <div className={classes.donutWrap}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={stats?.total ? donut : [{ name: "—", value: 1, color: theme.palette.divider }]} dataKey="value" innerRadius="64%" outerRadius="88%" paddingAngle={stats?.total ? 3 : 0} stroke="none" animationDuration={1000}>
                  {(stats?.total ? donut : [{ color: theme.palette.divider }]).map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                {stats?.total ? <ChartTooltip {...tooltipStyle} /> : null}
              </PieChart>
            </ResponsiveContainer>
            <div className={classes.donutCenter}>
              <div>
                <div className={classes.donutValue}>{Math.round((stats?.successRate || 0) * 100)}%</div>
                <div className={classes.donutLabel}>{t("kpiRate")}</div>
              </div>
            </div>
          </div>
          <div className={classes.legend}>
            <span><span className={classes.dot} style={{ background: OK_COLOR }} />{t("found")} {stats?.ok || 0}</span>
            <span><span className={classes.dot} style={{ background: FAIL_COLOR }} />{t("notFound")} {stats?.fail || 0}</span>
          </div>
          {(stats?.failReasons || []).length ? (
            <div className={classes.reasons}>
              {stats.failReasons.map((r) => (
                <div key={r.reason} className={classes.reason}><span>{r.reason}</span><strong>{r.total}</strong></div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className={classes.chartsThree}>
        <div className={classes.panel}>
          <div className={classes.panelTitle}>{t("hoursTitle")}</div>
          <div className={classes.panelSub}>{t("hoursSub")}</div>
          <div style={{ width: "100%", height: 170 }}>
            <ResponsiveContainer>
              <AreaChart data={hours} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="codeLogHours" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="hour" {...axis} tickFormatter={(h) => `${h}h`} interval={5} />
                <YAxis allowDecimals={false} {...axis} />
                <ChartTooltip {...tooltipStyle} labelFormatter={(h) => `${h}:00 – ${h}:59`} formatter={(v) => [v, t("lookups")]} />
                <Area type="monotone" dataKey="total" stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#codeLogHours)" animationDuration={1100} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={classes.panel} style={{ animationDelay: "60ms" }}>
          <div className={classes.panelTitle}>{t("productsTitle")}</div>
          <div className={classes.panelSub}>{t("productsSub")}</div>
          <Bars rows={stats?.byProduct || []} labelOf={(r) => r.name} max={maxProduct} />
        </div>
        <div className={classes.panel} style={{ animationDelay: "120ms" }}>
          <div className={classes.panelTitle}>{t("originTitle")}</div>
          <div className={classes.panelSub}>{t("originSub")}</div>
          {(stats?.byCountry || []).length ? (
            <Bars rows={stats.byCountry} labelOf={(r) => `${flagOf(r.country)}  ${countryName(r.country)}`} max={maxCountry} />
          ) : null}
          <Bars rows={stats?.byChannel || []} labelOf={(r) => channelLabel(r.channel)} max={maxChannel} />
        </div>
      </div>

      <div className={classes.header} style={{ marginTop: 0 }}>
        <div className={classes.panelTitle}>{t("recentTitle")}</div>
        <span className={classes.muted}>{t("recentSub")}</span>
      </div>
      {loading && !stats ? (
        <div className={classes.empty}>{tNegocio("loading")}</div>
      ) : (
        <LookupRows key={(stats?.recent || [])[0]?.id || "none"} rows={stats?.recent || []} onOpenContact={openContact} />
      )}

      <FullLogDialog open={open} onClose={() => setOpen(false)} onOpenContact={openContact} />
    </div>
  );
}
